import { useEffect, useMemo, useRef, useState } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from "react-native";
import { Search } from "lucide-react-native";
import { colors, typography } from "../theme/tokens";
import { useInputFontSize } from "../utils/inputFontSize";
import {
  COUNTRIES,
  getCountryName,
  type CountryMetadata,
} from "@src/packages/location/countryMetadata";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type MoonCountryPickerProps = {
  open: boolean;
  /** ISO-3166 alpha-2 of the current choice, highlighted in the list. */
  selectedCode: string | null;
  locale: "en" | "fr";
  onSelect: (country: CountryMetadata) => void;
  searchPlaceholder: string;
  noMatchesMessage: (query: string) => string;
  searchTestID?: string;
};

/**
 * The country list that drops down under a field, searchable by name or
 * code: the sibling of `MoonPhoneCountryPicker` for a country without a
 * dialling code. Mounted only while open, so its search starts empty and
 * focused every time.
 */
export function MoonCountryPicker({
  open,
  selectedCode,
  locale,
  onSelect,
  searchPlaceholder,
  noMatchesMessage,
  searchTestID,
}: MoonCountryPickerProps) {
  const searchFontSize = useInputFontSize();
  const searchInputRef = useRef<TextInput | null>(null);
  const capturePress = usePressCapture("MoonCountryPicker");
  const [countryQuery, setCountryQuery] = useState("");

  useEffect(() => {
    if (!open) {
      setCountryQuery("");
      return;
    }
    searchInputRef.current?.focus();
  }, [open]);

  const filteredCountries = useMemo(() => {
    const query = countryQuery.trim().toLocaleLowerCase();
    if (!query) return COUNTRIES;
    return COUNTRIES.filter(
      (candidate) =>
        getCountryName(candidate, locale).toLocaleLowerCase().includes(query) ||
        candidate.names.en.toLocaleLowerCase().includes(query) ||
        candidate.code.toLocaleLowerCase() === query,
    );
  }, [countryQuery, locale]);

  if (!open) return null;

  return (
    <View
      style={{
        marginTop: 6,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.12)",
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
        maxHeight: 280,
        ...Platform.select({
          ios: {
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.08,
            shadowRadius: 12,
          },
          android: { elevation: 6 },
          web: { boxShadow: "0 4px 16px rgba(0,0,0,0.08)" },
        }),
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          paddingHorizontal: 12,
          paddingVertical: 10,
          borderBottomWidth: 1,
          borderBottomColor: colors.border,
        }}
      >
        <Search size={13} color={colors.textFaint} strokeWidth={1.5} />
        <TextInput
          ref={searchInputRef}
          testID={searchTestID}
          value={countryQuery}
          onChangeText={setCountryQuery}
          placeholder={searchPlaceholder}
          placeholderTextColor={colors.textFaint}
          autoCorrect={false}
          spellCheck={false}
          autoFocus
          style={{
            flex: 1,
            fontSize: searchFontSize,
            color: colors.text,
            fontFamily: typography.families.sans,
            ...(Platform.OS === "web" ? { outlineStyle: "none" } : null),
          }}
        />
      </View>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        nestedScrollEnabled
        showsVerticalScrollIndicator={false}
      >
        {filteredCountries.map((country) => {
          const isSelected = country.code === selectedCode;
          const name = getCountryName(country, locale);
          return (
            <Pressable
              key={country.code}
              testID={`country-option-${country.code}`}
              accessibilityRole="button"
              accessibilityLabel={name}
              onPress={() => {
                capturePress(pressLabel(name), "select_country");
                onSelect(country);
                setCountryQuery("");
              }}
            >
              {({ pressed }) => (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    backgroundColor: isSelected
                      ? `${colors.warm}10`
                      : pressed
                        ? "rgba(0,0,0,0.04)"
                        : "transparent",
                  }}
                >
                  <Text style={{ fontSize: 16 }} accessibilityElementsHidden>
                    {country.flag}
                  </Text>
                  <Text
                    style={{
                      flex: 1,
                      fontFamily: isSelected
                        ? typography.families.sansMedium
                        : typography.families.sans,
                      fontSize: 14,
                      color: colors.text,
                    }}
                  >
                    {name}
                  </Text>
                </View>
              )}
            </Pressable>
          );
        })}
        {filteredCountries.length === 0 ? (
          <Text
            style={{
              paddingHorizontal: 12,
              paddingVertical: 12,
              fontFamily: typography.families.sans,
              fontSize: 13,
              color: colors.textFaint,
              textAlign: "center",
            }}
          >
            {noMatchesMessage(countryQuery)}
          </Text>
        ) : null}
      </ScrollView>
    </View>
  );
}
